import { useDispatch, useSelector } from "react-redux";
import { bTag, useModal } from "core";
import { CompetitionActionPanelActions } from "shared/reducers/CompetitionActionPanel";
import { CurrentCompetitionActions, CurrentCompetitionSelector } from "shared/reducers/CurrentCompetitionSlice";
import { useHistory } from "react-router-dom";
import { useProfile } from "@brg/auth";
import { CreateLinkTo } from "shared/constants/linkTo";
import { useCallback, useEffect, useMemo, useState } from "react";
import { API, Competition, Endpoints, ProjectAPI, Tag, TagsAPI } from "@brg/api";
import { useToasts } from "@brg/ui";
import urlApi from "shared/actions/urlApi";
import { useTranslation } from "react-i18next";
import useQueryState from "./useQueryState";
import { COMPETITION_TYPE, STATUSES } from "_competition/const";
import { isPromptModule } from "shared/pages/exploreHseContextsNew/const/promptFormsId";
import { getProjectRouteWithTagsParams } from "shared/helpers/getProjectRouteWithTagsParams";
import { getMaxLimitAttemptsPerUser } from "shared/helpers/getMaxLimitAttemptsPerUser";
import {
    formatDateTime,
    getProjectSubmissionStartDate,
    hasSubmittedPreRegistrationForm,
    isAwaitingModeratorApproval,
    isProjectSubmissionBeforeStart,
} from "_competition/helpers/preRegistrationAccess";

export type HandleCreateProjectOpts = {
    legalContestFormAfterPublish?: boolean;
};

const useCompetitionActions = (competition?: Competition) => {
    const dispatch = useDispatch();
    const { userAttempts } = useSelector(CurrentCompetitionSelector);
    const modal = useModal();
    const history = useHistory();
    const profile = useProfile();
    const { error, info } = useToasts();
    const { t, i18n } = useTranslation();
    const [{ isPreview }, setQueryParams] = useQueryState();

    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [shouldSendPrivate, setShouldSendPrivate] = useState(false);

    useEffect(() => {
        if (competition?.acceptPrivatePublished) {
            setShouldSendPrivate(true);
        }
    }, [competition?.acceptPrivatePublished]);

    const hasDocuments = useMemo(() => (!!competition?.nominations?.[0]?.documentsIds?.length), [competition])
    const canApplyWithoutProject = competition?.allowEmptyParticipantRequests;
    const needToFillForm = useMemo(() => {
        const rawType = competition?.competitionType;
        const hasCompetitionType = Number.isInteger(rawType);
        if (hasCompetitionType) {
            return rawType === COMPETITION_TYPE.WithQuestionnaire || !!competition?.isPaidCompetition;
        }
        return !!competition?.nominations?.[0]?.needToFillForm;
    }, [competition]);
    const competitionId = competition?.competitionId;

    const isPreRegistrationCompetition = useMemo(() => {
        const rawType = competition?.competitionType;
        const hasCompetitionType = Number.isInteger(rawType);
        if (hasCompetitionType) {
            return rawType === COMPETITION_TYPE.PreRegistration;
        }
        return !!competition?.isPreRegistration;
    }, [competition]);

    const isPreRegistration = useMemo(() => (
        isPreRegistrationCompetition &&
        !competition?.participantAccountIds?.includes(profile?.accountId) &&
        competition?.status === STATUSES.Open
    ), [profile, competition, isPreRegistrationCompetition])

    const tags: Tag[] = useMemo(
        () => [
            ...(competition?.regularTags || []),
            ...(competition?.requredTags || []),
            ...(competition?.tags || [])] || []
        , [competition])

    const tagString = useMemo(() => {
        return tags.map(tag => `#${bTag.title(tag)}`).join(' ')
    }, [tags])

    const isExam = competition?.lmsModuleProjectId;

    const isPaidApplication = useMemo(
        () => !!competition?.isPaidApplication,
        [competition?.isPaidApplication],
    );

    const isPaidParticipationContest = useMemo(
        () =>
            !!competition?.isPaidApplication ||
            !!competition?.isPaidCompetition,
        [competition?.isPaidApplication, competition?.isPaidCompetition],
    );

    const isPromptCompetition = useMemo(() => {
        return competition && isPromptModule(competition?.lmsModuleProjectId)
    } , [competition])

    const isAnimationCompetition = useMemo(() => {
        return competition && competition?.competitionId === '294dc67076b349268238883fa8dbd845'
    } , [competition])

    const storeCompetition = useCallback(() => {
        dispatch(CompetitionActionPanelActions.setCurrentCompetition(competition));
    }, [competition, dispatch]);

    const goToProfile = () => {
        storeCompetition();
        modal.hide()
        history.push(CreateLinkTo.designerNew(profile!));
    }

    const openProfile = () => {
        window.open(CreateLinkTo.designerNew(profile!), '_BLANK')
    }

    const resolveTagIdForCategory = async (categoryTag?: Tag): Promise<string | undefined> => {
        if (!categoryTag?.tagId) return undefined;
        let cat: any = categoryTag;
        if (!cat?.additionalTags?.length) {
            const raw = await TagsAPI.GetTagsTree({
                groupIds: [cat.tagId],
                status: [0],
                lang: i18n.language,
                isHeaderIgnored: true
            }).then((r: any) => r.json()).catch(() => null);
            const list = Array.isArray(raw) ? raw : [];
            cat = list.find((c: any) => c?.tagId === categoryTag.tagId) || list[0] || cat;
        }
        const children: any[] = cat?.additionalTags || [];
        if (!children.length) return undefined;
        const whiteOnly = !!cat?.hasEduTags;
        const pick = whiteOnly
            ? children.find((c) => c.isEducationTag)
            : children.find((c) => !c.isEducationTag);
        return pick?.tagId;
    };

    const handleCreateProject = async (target = '_SELF', opts?: HandleCreateProjectOpts) => {
        const maxAttemptsPerUser = getMaxLimitAttemptsPerUser(competition);
        if (
            maxAttemptsPerUser != null
            && userAttempts?.competitionId === competition?.competitionId
            && userAttempts.current >= maxAttemptsPerUser
        ) {
            error(t('competition.max_attempts_per_user_reached'));
            return;
        }

        storeCompetition();
        if (opts?.legalContestFormAfterPublish && competition?.competitionId) {
            dispatch(
                CompetitionActionPanelActions.setLegalContestDeferFormPublishCompetitionId(competition.competitionId),
            );
        } else {
            dispatch(CompetitionActionPanelActions.setLegalContestDeferFormPublishCompetitionId(undefined));
        }
        const isByCategory = !!(competition as any)?.isByCategory;
        const blankWin = target === '_BLANK' ? window.open('about:blank', target) : null;

        const pickTagId = async (tag?: Tag): Promise<string | undefined> => {
            if (!tag) return undefined;
            if (isByCategory && (tag as any).isTagCategory) return await resolveTagIdForCategory(tag);
            return tag.tagId;
        };

        const requiredTagId = (await pickTagId(competition?.requredTags?.[0])) || competition?.requredTagIds?.[0];
        const regularTagId = (await pickTagId(competition?.regularTags?.[0])) || competition?.regularTagIds?.[0];
        const tagId = requiredTagId
            ? undefined
            : (regularTagId || (isAnimationCompetition ? '98d101eb8464477bb207c74f63c47977' : undefined));
        const backToId = (isPromptCompetition ? competition?.lmsModuleProjectId : competition?.competitionId) ?? undefined;
        const routeToBack = isPromptCompetition ? 'project' : 'competition';
        const url = getProjectRouteWithTagsParams(CreateLinkTo.projectCreate(), requiredTagId, tagId, backToId, routeToBack);

        if (target === '_BLANK') {
            if (blankWin) {
                blankWin.location.href = url;
            } else {
                window.open(url, target);
            }
        } else {
            history.push(url);
        }
        modal.hide();
    }

    const shouldDisplayNoProjectsCheckbox = useMemo(
        () => (!selectedProjects?.length && canApplyWithoutProject)
        , [selectedProjects, canApplyWithoutProject])

    const handleSubmitFor = useCallback(({
        projectName,
    }: { projectName: string }) => {
        modal.show('confirmation', {
            projectName,
            competitionId: competition?.competitionId,
            competitionPrice: competition?.competitionInfo?.applicationPrice,
            projectId: competition?.lmsModuleProjectId,
            selectedProjectId: selectedProjects[0],
            onConfirm: () => {
                console.log('Переход к оплате за проект:', projectName);
            },
            onCancel: () => {
                console.log('Отмена отправки');
            },
            onPaymentSuccess: () => {
                console.log('Оплата прошла успешно, проект отправлен на конкурс');
            },
            onPaymentFail: () => {
                modal.show('paymentErrorModal', {
                    title: 'Ошибка',
                    message: 'Оплата отклонена провайдером',
                    returnButtonText: 'Вернуться к модулю'
                });
            }
        });
    }, [selectedProjects, competition, modal]);


    const getLegalsModal = useCallback((flag: boolean, fn: Function, forceProjects?: string[]) => {
        if (flag && competitionId && competition && competition.nominationsID && needToFillForm) {
            modal.show(/*isPreRegistration ? 'competition-custom-legals' : */'competition-form', {
                projects: forceProjects ?? selectedProjects,
                nominationIds: [...competition.nominationsID],
                competitionId,
                hasDocuments,
                isPreRegistration,
                //@ts-ignore
                registrationDeadline: competition?.formAnswerApplyEnd,
                hideProject: shouldSendPrivate,
                callback: () => {
                    fn(competitionId);
                },
            });
        } else fn(competitionId);
    }, [competition, modal, competitionId, selectedProjects, hasDocuments, needToFillForm, isPreRegistration, shouldSendPrivate])

    const onApplyClick = async ({
        forceSelectedProjectId,
        shouldPublish,
        projectId,
        makePrivate,
    }: { forceSelectedProjectId?: string, shouldPublish?: boolean, projectId?: string, makePrivate?: boolean }) => {
        try {
            // Используем переданный selectedProjectId или текущий selectedProjects
            const projectsToUse = forceSelectedProjectId ? [forceSelectedProjectId] : selectedProjects;

            if (!forceSelectedProjectId) {
                const res = await new Promise((resolve, reject) => {
                    modal.show('sendConfirmation', {
                        onConfirm: () => {
                            (shouldPublish ? ProjectAPI.UpdateStatus({
                                projectStatus: (makePrivate || shouldSendPrivate) ? 10 : 1,
                                projectId,
                            }) : Promise.resolve()).then((r) => {
                                if (shouldPublish) {
                                    if (r?.status !== 200) {
                                        throw new Error('Не удалось опубликовать проект');
                                    } else {
                                        setQueryParams({ isPreview: undefined });
                                        history.push(CreateLinkTo.project(projectId));
                                    }
                                }
                                resolve(true);
                            })
                        },
                        onCancel: () => {
                            modal.back();
                            resolve(false);
                        },
                    });
                })
                if (!res) return;
            }

            //@ts-ignore
            if (competition?.maxParticipantsCount && projectsToUse?.length > maxProjects) {
                error(`Вы не можете подать проекты на конкурс, так как модератор установил ограничение на количество. Обратитесь к модератору.`)
                return;
            }

            const maxAttemptsPerUser = getMaxLimitAttemptsPerUser(competition);
            const currentAttemptsPerUser = userAttempts?.competitionId === competition?.competitionId
                ? userAttempts.current
                : 0;
            if (
                maxAttemptsPerUser != null
                && currentAttemptsPerUser + (projectsToUse?.length ?? 0) > maxAttemptsPerUser
            ) {
                error(t('competition.max_attempts_per_user_reached'));
                return;
            }
            if (isPreRegistrationCompetition) {
                if (!hasSubmittedPreRegistrationForm(competition, profile?.accountId)) {
                    error(t('competition.pre_registration_required'));
                    return;
                }

                if (isAwaitingModeratorApproval(competition, profile?.accountId)) {
                    error(t('competition.apply_project_pending_moderation'));
                    return;
                }

                if (isProjectSubmissionBeforeStart(competition, profile?.accountId)) {
                    error(t('competition.apply_project_not_started', {
                        date: formatDateTime(getProjectSubmissionStartDate(competition)),
                    }));
                    return;
                }
            }

            // проверка дедлайна приемки проектов
            const endDate = new Date(competition?.endDate || 0);
            const now = new Date();
            const isDeprecated = endDate < now
            if (isDeprecated) {
                modal.show('competition-sent-error', {
                    competitionEndDate: competition?.endDate,
                    onClick: () => {
                        window.location.reload()
                    },
                    onClose: () => window.location.reload(),
                });
                return;
            }

            const questionnaireCompletedOutsideCompetitionModal = !!forceSelectedProjectId;

            if (
                (projectsToUse?.length > 1 && needToFillForm) ||
                !needToFillForm ||
                questionnaireCompletedOutsideCompetitionModal
            ) {
                let res;
                const projects =
                    needToFillForm && !questionnaireCompletedOutsideCompetitionModal
                        ? [...projectsToUse]?.slice(1)
                        : projectsToUse;
                if (isExam) {
                    res = await Promise.all(projects.map((value, idx) => {
                        return API.request()
                            .endpoint(Endpoints.competitions)
                            .url('/api/Competition/ApplyProjectToExam')
                            .post()
                            .addQuery('competitionId', competition?.competitionId || '')
                            .addQuery('ProjectId', value)
                            .send()
                    }))
                } else {
                    res = await Promise.all(projects.map((value, idx) => {
                        return urlApi().applyProjectToCompetition({
                            nominationId: competition?.nominationsID?.[0] || '',
                            projectId: value,
                            makePrivate: makePrivate || shouldSendPrivate,
                        });
                    }));
                }
                if (res?.some((r: any) => r.status !== 200)) {
                    throw new Error("Что-то пошло не так");
                }
            }

            if (!forceSelectedProjectId) {
                setSelectedProjects([]);
            }

            if (competitionId) {
                const appliedCount = projectsToUse?.length ?? 0;
                const maxAttemptsAfterApply = getMaxLimitAttemptsPerUser(competition);
                if (appliedCount > 0 && maxAttemptsAfterApply) {
                    dispatch(CurrentCompetitionActions.refreshAttemptsCheck({
                        increment: appliedCount,
                        competitionId,
                        max: maxAttemptsAfterApply,
                    }));
                }

                if (isExam) {
                    info(`Проект успешно подан на ${isPromptCompetition ? 'конкурс' : 'экзамен'}`)
                    return modal.show('competition-sent', {
                        competitionId,
                        isExam: isExam,
                        competition: competition,
                        isPaidApplication: isPaidApplication,
                        onClick: () => {
                            if (isPromptCompetition) {
                                window.location.href = process.env.REACT_APP_URL_MEDIIIA_COM + '/ai' + CreateLinkTo.project(competition?.lmsModuleProjectId)
                                return;
                            }
                            window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.project(competition?.lmsModuleProjectId)
                        },
                        onClose: () => window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.project(competition?.lmsModuleProjectId),
                        onBackToCompetition: () => {
                            if (isPromptCompetition) {
                                window.location.href = process.env.REACT_APP_URL_MEDIIIA_COM + '/ai' + CreateLinkTo.competition(competition?.competitionId)
                                return;
                            }
                            window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.competition(competition?.competitionId)
                        },
                    });
                }

                dispatch(CompetitionActionPanelActions.resetCurrentCompetition());
                modal.show('competitionProjectSent', ({
                    competitionId: competition?.competitionId,
                    projectId: projectsToUse[0],
                    onClose: () => {
                        history.push(CreateLinkTo.competition(competition?.competitionId || ''));
                    },
                    onClick: () => {
                        modal.hide();
                        history.push(CreateLinkTo.competition(competition?.competitionId || ''));
                    }
                }));
            }

        } catch (e) {
            error(String(e));
        }
    };

    return {
        storeCompetition,
        goToProfile,
        openProfile,
        handleCreateProject,
        selectedProjects,
        setSelectedProjects,
        isPaidApplication,
        shouldDisplayNoProjectsCheckbox,
        canApplyWithoutProject,
        needToFillForm,
        isPromptCompetition,
        isPreRegistration,
        handleSubmitFor,
        onApplyClick,
        getLegalsModal,
        hasDocuments,
        isExam,
        tagString,
        tags,
        shouldSendPrivate,
        setShouldSendPrivate,
        isPaidParticipationContest,
    }
}

export default useCompetitionActions;import { useDispatch, useSelector } from "react-redux";
import { bTag, useModal } from "core";
import { CompetitionActionPanelActions } from "shared/reducers/CompetitionActionPanel";
import { CurrentCompetitionActions, CurrentCompetitionSelector } from "shared/reducers/CurrentCompetitionSlice";
import { useHistory } from "react-router-dom";
import { useProfile } from "@brg/auth";
import { CreateLinkTo } from "shared/constants/linkTo";
import { useCallback, useEffect, useMemo, useState } from "react";
import { API, Competition, Endpoints, ProjectAPI, Tag, TagsAPI } from "@brg/api";
import { useToasts } from "@brg/ui";
import urlApi from "shared/actions/urlApi";
import { useTranslation } from "react-i18next";
import useQueryState from "./useQueryState";
import { COMPETITION_TYPE, STATUSES } from "_competition/const";
import { isPromptModule } from "shared/pages/exploreHseContextsNew/const/promptFormsId";
import { getProjectRouteWithTagsParams } from "shared/helpers/getProjectRouteWithTagsParams";
import { getMaxLimitAttemptsPerUser } from "shared/helpers/getMaxLimitAttemptsPerUser";
import {
    formatDateTime,
    getProjectSubmissionStartDate,
    hasSubmittedPreRegistrationForm,
    isAwaitingModeratorApproval,
    isProjectSubmissionBeforeStart,
} from "_competition/helpers/preRegistrationAccess";

export type HandleCreateProjectOpts = {
    legalContestFormAfterPublish?: boolean;
};

const useCompetitionActions = (competition?: Competition) => {
    const dispatch = useDispatch();
    const { userAttempts } = useSelector(CurrentCompetitionSelector);
    const modal = useModal();
    const history = useHistory();
    const profile = useProfile();
    const { error, info } = useToasts();
    const { t, i18n } = useTranslation();
    const [{ isPreview }, setQueryParams] = useQueryState();

    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [shouldSendPrivate, setShouldSendPrivate] = useState(false);

    useEffect(() => {
        if (competition?.acceptPrivatePublished) {
            setShouldSendPrivate(true);
        }
    }, [competition?.acceptPrivatePublished]);

    const hasDocuments = useMemo(() => (!!competition?.nominations?.[0]?.documentsIds?.length), [competition])
    const canApplyWithoutProject = competition?.allowEmptyParticipantRequests;
    const needToFillForm = useMemo(() => {
        const rawType = competition?.competitionType;
        const hasCompetitionType = Number.isInteger(rawType);
        if (hasCompetitionType) {
            return rawType === COMPETITION_TYPE.WithQuestionnaire || !!competition?.isPaidCompetition;
        }
        return !!competition?.nominations?.[0]?.needToFillForm;
    }, [competition]);
    const competitionId = competition?.competitionId;

    const isPreRegistrationCompetition = useMemo(() => {
        const rawType = competition?.competitionType;
        const hasCompetitionType = Number.isInteger(rawType);
        if (hasCompetitionType) {
            return rawType === COMPETITION_TYPE.PreRegistration;
        }
        return !!competition?.isPreRegistration;
    }, [competition]);

    const isPreRegistration = useMemo(() => (
        isPreRegistrationCompetition &&
        !competition?.participantAccountIds?.includes(profile?.accountId) &&
        competition?.status === STATUSES.Open
    ), [profile, competition, isPreRegistrationCompetition])

    const tags: Tag[] = useMemo(
        () => [
            ...(competition?.regularTags || []),
            ...(competition?.requredTags || []),
            ...(competition?.tags || [])] || []
        , [competition])

    const tagString = useMemo(() => {
        return tags.map(tag => `#${bTag.title(tag)}`).join(' ')
    }, [tags])

    const isExam = competition?.lmsModuleProjectId;

    const isPaidApplication = useMemo(
        () => !!competition?.isPaidApplication,
        [competition?.isPaidApplication],
    );

    const isPaidParticipationContest = useMemo(
        () =>
            !!competition?.isPaidApplication ||
            !!competition?.isPaidCompetition,
        [competition?.isPaidApplication, competition?.isPaidCompetition],
    );

    const isPromptCompetition = useMemo(() => {
        return competition && isPromptModule(competition?.lmsModuleProjectId)
    } , [competition])

    const isAnimationCompetition = useMemo(() => {
        return competition && competition?.competitionId === '294dc67076b349268238883fa8dbd845'
    } , [competition])

    const storeCompetition = useCallback(() => {
        dispatch(CompetitionActionPanelActions.setCurrentCompetition(competition));
    }, [competition, dispatch]);

    const goToProfile = () => {
        storeCompetition();
        modal.hide()
        history.push(CreateLinkTo.designerNew(profile!));
    }

    const openProfile = () => {
        window.open(CreateLinkTo.designerNew(profile!), '_BLANK')
    }

    const resolveTagIdForCategory = async (categoryTag?: Tag): Promise<string | undefined> => {
        if (!categoryTag?.tagId) return undefined;
        let cat: any = categoryTag;
        if (!cat?.additionalTags?.length) {
            const raw = await TagsAPI.GetTagsTree({
                groupIds: [cat.tagId],
                status: [0],
                lang: i18n.language,
                isHeaderIgnored: true
            }).then((r: any) => r.json()).catch(() => null);
            const list = Array.isArray(raw) ? raw : [];
            cat = list.find((c: any) => c?.tagId === categoryTag.tagId) || list[0] || cat;
        }
        const children: any[] = cat?.additionalTags || [];
        if (!children.length) return undefined;
        const whiteOnly = !!cat?.hasEduTags;
        const pick = whiteOnly
            ? children.find((c) => c.isEducationTag)
            : children.find((c) => !c.isEducationTag);
        return pick?.tagId;
    };

    const handleCreateProject = async (target = '_SELF', opts?: HandleCreateProjectOpts) => {
        const maxAttemptsPerUser = getMaxLimitAttemptsPerUser(competition);
        if (
            maxAttemptsPerUser != null
            && userAttempts?.competitionId === competition?.competitionId
            && userAttempts.current >= maxAttemptsPerUser
        ) {
            error(t('competition.max_attempts_per_user_reached'));
            return;
        }

        storeCompetition();
        if (opts?.legalContestFormAfterPublish && competition?.competitionId) {
            dispatch(
                CompetitionActionPanelActions.setLegalContestDeferFormPublishCompetitionId(competition.competitionId),
            );
        } else {
            dispatch(CompetitionActionPanelActions.setLegalContestDeferFormPublishCompetitionId(undefined));
        }
        const isByCategory = !!(competition as any)?.isByCategory;
        const blankWin = target === '_BLANK' ? window.open('about:blank', target) : null;

        const pickTagId = async (tag?: Tag): Promise<string | undefined> => {
            if (!tag) return undefined;
            if (isByCategory && (tag as any).isTagCategory) return await resolveTagIdForCategory(tag);
            return tag.tagId;
        };

        const requiredTagId = (await pickTagId(competition?.requredTags?.[0])) || competition?.requredTagIds?.[0];
        const regularTagId = (await pickTagId(competition?.regularTags?.[0])) || competition?.regularTagIds?.[0];
        const tagId = requiredTagId
            ? undefined
            : (regularTagId || (isAnimationCompetition ? '98d101eb8464477bb207c74f63c47977' : undefined));
        const backToId = (isPromptCompetition ? competition?.lmsModuleProjectId : competition?.competitionId) ?? undefined;
        const routeToBack = isPromptCompetition ? 'project' : 'competition';
        const url = getProjectRouteWithTagsParams(CreateLinkTo.projectCreate(), requiredTagId, tagId, backToId, routeToBack);

        if (target === '_BLANK') {
            if (blankWin) {
                blankWin.location.href = url;
            } else {
                window.open(url, target);
            }
        } else {
            history.push(url);
        }
        modal.hide();
    }

    const shouldDisplayNoProjectsCheckbox = useMemo(
        () => (!selectedProjects?.length && canApplyWithoutProject)
        , [selectedProjects, canApplyWithoutProject])

    const handleSubmitFor = useCallback(({
        projectName,
    }: { projectName: string }) => {
        modal.show('confirmation', {
            projectName,
            competitionId: competition?.competitionId,
            competitionPrice: competition?.competitionInfo?.applicationPrice,
            projectId: competition?.lmsModuleProjectId,
            selectedProjectId: selectedProjects[0],
            onConfirm: () => {
                console.log('Переход к оплате за проект:', projectName);
            },
            onCancel: () => {
                console.log('Отмена отправки');
            },
            onPaymentSuccess: () => {
                console.log('Оплата прошла успешно, проект отправлен на конкурс');
            },
            onPaymentFail: () => {
                modal.show('paymentErrorModal', {
                    title: 'Ошибка',
                    message: 'Оплата отклонена провайдером',
                    returnButtonText: 'Вернуться к модулю'
                });
            }
        });
    }, [selectedProjects, competition, modal]);


    const getLegalsModal = useCallback((flag: boolean, fn: Function, forceProjects?: string[]) => {
        if (flag && competitionId && competition && competition.nominationsID && needToFillForm) {
            modal.show(/*isPreRegistration ? 'competition-custom-legals' : */'competition-form', {
                projects: forceProjects ?? selectedProjects,
                nominationIds: [...competition.nominationsID],
                competitionId,
                hasDocuments,
                isPreRegistration,
                //@ts-ignore
                registrationDeadline: competition?.formAnswerApplyEnd,
                hideProject: shouldSendPrivate,
                callback: () => {
                    fn(competitionId);
                },
            });
        } else fn(competitionId);
    }, [competition, modal, competitionId, selectedProjects, hasDocuments, needToFillForm, isPreRegistration, shouldSendPrivate])

    const onApplyClick = async ({
        forceSelectedProjectId,
        shouldPublish,
        projectId,
        makePrivate,
    }: { forceSelectedProjectId?: string, shouldPublish?: boolean, projectId?: string, makePrivate?: boolean }) => {
        try {
            // Используем переданный selectedProjectId или текущий selectedProjects
            const projectsToUse = forceSelectedProjectId ? [forceSelectedProjectId] : selectedProjects;

            if (!forceSelectedProjectId) {
                const res = await new Promise((resolve, reject) => {
                    modal.show('sendConfirmation', {
                        onConfirm: () => {
                            (shouldPublish ? ProjectAPI.UpdateStatus({
                                projectStatus: (makePrivate || shouldSendPrivate) ? 10 : 1,
                                projectId,
                            }) : Promise.resolve()).then((r) => {
                                if (shouldPublish) {
                                    if (r?.status !== 200) {
                                        throw new Error('Не удалось опубликовать проект');
                                    } else {
                                        setQueryParams({ isPreview: undefined });
                                        history.push(CreateLinkTo.project(projectId));
                                    }
                                }
                                resolve(true);
                            })
                        },
                        onCancel: () => {
                            modal.back();
                            resolve(false);
                        },
                    });
                })
                if (!res) return;
            }

            //@ts-ignore
            if (competition?.maxParticipantsCount && projectsToUse?.length > maxProjects) {
                error(`Вы не можете подать проекты на конкурс, так как модератор установил ограничение на количество. Обратитесь к модератору.`)
                return;
            }

            const maxAttemptsPerUser = getMaxLimitAttemptsPerUser(competition);
            const currentAttemptsPerUser = userAttempts?.competitionId === competition?.competitionId
                ? userAttempts.current
                : 0;
            if (
                maxAttemptsPerUser != null
                && currentAttemptsPerUser + (projectsToUse?.length ?? 0) > maxAttemptsPerUser
            ) {
                error(t('competition.max_attempts_per_user_reached'));
                return;
            }
            if (isPreRegistrationCompetition) {
                if (!hasSubmittedPreRegistrationForm(competition, profile?.accountId)) {
                    error(t('competition.pre_registration_required'));
                    return;
                }

                if (isAwaitingModeratorApproval(competition, profile?.accountId)) {
                    error(t('competition.apply_project_pending_moderation'));
                    return;
                }

                if (isProjectSubmissionBeforeStart(competition, profile?.accountId)) {
                    error(t('competition.apply_project_not_started', {
                        date: formatDateTime(getProjectSubmissionStartDate(competition)),
                    }));
                    return;
                }
            }

            // проверка дедлайна приемки проектов
            const endDate = new Date(competition?.endDate || 0);
            const now = new Date();
            const isDeprecated = endDate < now
            if (isDeprecated) {
                modal.show('competition-sent-error', {
                    competitionEndDate: competition?.endDate,
                    onClick: () => {
                        window.location.reload()
                    },
                    onClose: () => window.location.reload(),
                });
                return;
            }

            const questionnaireCompletedOutsideCompetitionModal = !!forceSelectedProjectId;

            if (
                (projectsToUse?.length > 1 && needToFillForm) ||
                !needToFillForm ||
                questionnaireCompletedOutsideCompetitionModal
            ) {
                let res;
                const projects =
                    needToFillForm && !questionnaireCompletedOutsideCompetitionModal
                        ? [...projectsToUse]?.slice(1)
                        : projectsToUse;
                if (isExam) {
                    res = await Promise.all(projects.map((value, idx) => {
                        return API.request()
                            .endpoint(Endpoints.competitions)
                            .url('/api/Competition/ApplyProjectToExam')
                            .post()
                            .addQuery('competitionId', competition?.competitionId || '')
                            .addQuery('ProjectId', value)
                            .send()
                    }))
                } else {
                    res = await Promise.all(projects.map((value, idx) => {
                        return urlApi().applyProjectToCompetition({
                            nominationId: competition?.nominationsID?.[0] || '',
                            projectId: value,
                            makePrivate: makePrivate || shouldSendPrivate,
                        });
                    }));
                }
                if (res?.some((r: any) => r.status !== 200)) {
                    throw new Error("Что-то пошло не так");
                }
            }

            if (!forceSelectedProjectId) {
                setSelectedProjects([]);
            }

            if (competitionId) {
                const appliedCount = projectsToUse?.length ?? 0;
                const maxAttemptsAfterApply = getMaxLimitAttemptsPerUser(competition);
                if (appliedCount > 0 && maxAttemptsAfterApply) {
                    dispatch(CurrentCompetitionActions.refreshAttemptsCheck({
                        increment: appliedCount,
                        competitionId,
                        max: maxAttemptsAfterApply,
                    }));
                }

                if (isExam) {
                    info(`Проект успешно подан на ${isPromptCompetition ? 'конкурс' : 'экзамен'}`)
                    return modal.show('competition-sent', {
                        competitionId,
                        isExam: isExam,
                        competition: competition,
                        isPaidApplication: isPaidApplication,
                        onClick: () => {
                            if (isPromptCompetition) {
                                window.location.href = process.env.REACT_APP_URL_MEDIIIA_COM + '/ai' + CreateLinkTo.project(competition?.lmsModuleProjectId)
                                return;
                            }
                            window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.project(competition?.lmsModuleProjectId)
                        },
                        onClose: () => window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.project(competition?.lmsModuleProjectId),
                        onBackToCompetition: () => {
                            if (isPromptCompetition) {
                                window.location.href = process.env.REACT_APP_URL_MEDIIIA_COM + '/ai' + CreateLinkTo.competition(competition?.competitionId)
                                return;
                            }
                            window.location.href = process.env.REACT_APP_URL_HSEDESIGN_LMS + CreateLinkTo.competition(competition?.competitionId)
                        },
                    });
                }

                dispatch(CompetitionActionPanelActions.resetCurrentCompetition());
                modal.show('competitionProjectSent', ({
                    competitionId: competition?.competitionId,
                    projectId: projectsToUse[0],
                    onClose: () => {
                        history.push(CreateLinkTo.competition(competition?.competitionId || ''));
                    },
                    onClick: () => {
                        modal.hide();
                        history.push(CreateLinkTo.competition(competition?.competitionId || ''));
                    }
                }));
            }

        } catch (e) {
            error(String(e));
        }
    };

    return {
        storeCompetition,
        goToProfile,
        openProfile,
        handleCreateProject,
        selectedProjects,
        setSelectedProjects,
        isPaidApplication,
        shouldDisplayNoProjectsCheckbox,
        canApplyWithoutProject,
        needToFillForm,
        isPromptCompetition,
        isPreRegistration,
        handleSubmitFor,
        onApplyClick,
        getLegalsModal,
        hasDocuments,
        isExam,
        tagString,
        tags,
        shouldSendPrivate,
        setShouldSendPrivate,
        isPaidParticipationContest,
    }
}

export default useCompetitionActions;